import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren
} from "react";
import {
  Animated,
  Easing,
  Platform,
  StyleSheet,
  View,
  useWindowDimensions
} from "react-native";
import { Image as ExpoImage } from "expo-image";
import { LinearGradient } from "expo-linear-gradient";
import { useFocusEffect } from "expo-router";

import {
  ARTWORK_BACKDROP_MOTION,
  CARD_BACKDROP_REVEAL_MS,
  getBackdropTransitionMotion,
  resolveBackdropTransitionOrigin
} from "@/design/backdropMotion";
import {
  ARTWORK_FOCAL_Y,
  PAGE_SCRIM_MID_STOP,
  pageScrimStops
} from "@/design/backdropTone";
import { brandBackdropSource } from "@/design/brandBackdrop";
import {
  getDecodedImageUri,
  hasDecodedImage,
  markImageDecoded
} from "@/lib/imageReadiness";
import { useAppComposed, useReadinessHold } from "@/providers/ReadinessGate";
import {
  getWebPersistentBackdropSnapshot,
  requestWebPersistentBackdrop
} from "@/components/webPersistentBackdrop";

type BackdropSlotState = {
  failed: boolean;
  uri: string | null;
};

/**
 * Native twin of the web compositor's `background-position`. The focal anchor
 * is per route — a program cover wants it off its wordmark, brand art and
 * stage stills want the centred default — so it is threaded through the
 * provider rather than baked in. See design/backdropTone.ts.
 */
function artworkContentPosition(focalY: number | null) {
  return { left: "50%", top: `${focalY ?? ARTWORK_FOCAL_Y}%` } as const;
}

type BackdropContextValue = {
  cardHandoff: {
    opacity: Animated.Value;
    uri: string | null;
  };
  /** The focused route's requested focal anchor; null means the centred default. */
  focalY: number | null;
  onError: (slot: number, uri: string) => void;
  onLoad: (slot: number, uri: string) => void;
  request: (uri: string | null, focalY?: number | null) => void;
  slots: Array<BackdropSlotState & { opacity: Animated.Value }>;
};

const BackdropContext = createContext<BackdropContextValue | null>(null);

function resolvedUri(uri: string | null): string | null {
  if (!uri || Platform.OS !== "web") return uri;
  return getDecodedImageUri(uri) ?? uri;
}

function initiallySettled(uri: string | null): boolean {
  return !uri || (Platform.OS === "web" && hasDecodedImage(uri));
}

/**
 * Owns the page artwork outside the router, so navigation never unmounts the
 * visible backdrop. Focused routes only request a URI; this provider loads it
 * and crossfades the one persistent surface from the previous image.
 */
export function PageBackdropProvider({ children }: PropsWithChildren) {
  const appComposed = useAppComposed();
  const slotOpacities = useRef<[Animated.Value, Animated.Value]>([
    new Animated.Value(1),
    new Animated.Value(0)
  ]).current;
  const animationRef = useRef<Animated.CompositeAnimation | null>(null);
  const cardHandoffAnimationRef =
    useRef<Animated.CompositeAnimation | null>(null);
  const cardHandoffOpacity = useRef(new Animated.Value(0)).current;
  const activeSlotRef = useRef(0);
  const pendingSlotRef = useRef<number | null>(null);
  const transitionMotionRef = useRef(ARTWORK_BACKDROP_MOTION);
  const targetUriRef = useRef<string | null>(null);
  const slotUrisRef = useRef<[string | null, string | null]>([null, null]);
  // Native image slots stay mounted between routes. Track which URI each
  // mounted slot has actually loaded so returning to a hidden, already-painted
  // image does not wait forever for an onLoad event that will not fire again.
  const loadedSlotUrisRef = useRef<[string | null, string | null]>([null, null]);
  const [slots, setSlots] = useState<[BackdropSlotState, BackdropSlotState]>([
    { failed: false, uri: null },
    { failed: false, uri: null }
  ]);
  const [settled, setSettled] = useState(true);
  const [transitionPending, setTransitionPending] = useState(false);
  const [cardHandoffUri, setCardHandoffUri] = useState<string | null>(null);
  const targetFocalRef = useRef<number | null>(null);
  const [focalY, setFocalY] = useState<number | null>(null);

  useReadinessHold(`page-backdrop:${targetUriRef.current ?? "none"}`, settled);

  const request = useCallback((
    requestedUri: string | null,
    requestedFocalY: number | null = null
  ) => {
    const nextUri = resolvedUri(requestedUri);
    const transitionOrigin = resolveBackdropTransitionOrigin(
      {
        composed: appComposed,
        uri: targetUriRef.current
      },
      Platform.OS === "web"
        ? getWebPersistentBackdropSnapshot()
        : undefined
    );
    const settleWebBackdrop = () => {
      setSettled(false);
      void requestWebPersistentBackdrop(
        nextUri,
        requestedFocalY,
        (settledUri, loaded) => {
          if (targetUriRef.current !== settledUri) return;
          if (loaded && settledUri) markImageDecoded(settledUri);
          // A failed image resolves to the complete dark fallback state.
          setSettled(true);
        }
      );
    };

    // The anchor tracks the focused route even when the artwork does not change
    // (returning to a cover page from one of its show pages reuses no bitmap but
    // must re-anchor). The web compositor handles an anchor-only change inside
    // its own early return, without a blend.
    if (targetFocalRef.current !== requestedFocalY) {
      targetFocalRef.current = requestedFocalY;
      setFocalY(requestedFocalY);
    }

    if (targetUriRef.current === nextUri) {
      // Expo Router can remount this React provider during browser history
      // while the body-level web compositor deliberately survives. Its real
      // state may therefore differ from this fresh ref even when the focused
      // route requests the ref's initial value (notably Account requesting
      // null). Always reconcile duplicate web requests with that compositor;
      // it owns its own idempotence.
      if (Platform.OS === "web") settleWebBackdrop();
      return;
    }

    animationRef.current?.stop();
    cardHandoffAnimationRef.current?.stop();

    // If navigation interrupts an active blend, keep the established outgoing
    // slot fully visible and reuse the other hidden slot for the newest target.
    if (pendingSlotRef.current != null) {
      slotOpacities[activeSlotRef.current].setValue(1);
      slotOpacities[1 - activeSlotRef.current].setValue(0);
      cardHandoffOpacity.setValue(0);
      setCardHandoffUri(null);
      pendingSlotRef.current = null;
      setTransitionPending(false);
    }

    const outgoingSlot = activeSlotRef.current;
    const outgoingUri = transitionOrigin.uri;
    targetUriRef.current = nextUri;
    if (Platform.OS === "web") {
      settleWebBackdrop();
    }

    // Initial composition happens invisibly beneath the curtain and should be
    // fully settled before reveal. Put it directly into the active slot.
    if (!transitionOrigin.composed) {
      const slotAlreadyLoaded = loadedSlotUrisRef.current[outgoingSlot] === nextUri;
      if (Platform.OS !== "web") {
        setSettled(initiallySettled(nextUri) || slotAlreadyLoaded);
      }
      if (slotUrisRef.current[outgoingSlot] !== nextUri) {
        loadedSlotUrisRef.current[outgoingSlot] = null;
      }
      slotUrisRef.current[outgoingSlot] = nextUri;
      setSlots((current) => {
        const updated = [...current] as [BackdropSlotState, BackdropSlotState];
        updated[outgoingSlot] = { failed: false, uri: nextUri };
        return updated;
      });
      slotOpacities[outgoingSlot].setValue(1);
      setTransitionPending(false);
      return;
    }

    // Every later navigation updates only the invisible slot. Both image
    // components keep stable React keys and compositor layers for the entire
    // app session, including browser Back/Forward restoration.
    const incomingSlot = 1 - outgoingSlot;
    transitionMotionRef.current = getBackdropTransitionMotion(
      outgoingUri,
      nextUri
    );
    // iOS native-stack routes carry their own static backdrop through the
    // push/pop transition. A card handoff there would animate the home card
    // against a page background that deliberately does not animate, making
    // the card look like a separate late-loading layer. Android and web use
    // the persistent backdrop transition, so their cards still join it.
    const handoffUri =
      Platform.OS !== "ios" && outgoingUri && nextUri ? outgoingUri : null;
    cardHandoffOpacity.setValue(handoffUri ? 1 : 0);
    setCardHandoffUri(handoffUri);
    const slotAlreadyLoaded = loadedSlotUrisRef.current[incomingSlot] === nextUri;
    if (Platform.OS !== "web") {
      setSettled(initiallySettled(nextUri) || slotAlreadyLoaded);
    }
    slotOpacities[incomingSlot].setValue(0);
    if (slotUrisRef.current[incomingSlot] !== nextUri) {
      loadedSlotUrisRef.current[incomingSlot] = null;
    }
    slotUrisRef.current[incomingSlot] = nextUri;
    setSlots((current) => {
      const updated = [...current] as [BackdropSlotState, BackdropSlotState];
      updated[incomingSlot] = { failed: false, uri: nextUri };
      return updated;
    });
    pendingSlotRef.current = incomingSlot;
    setTransitionPending(true);
  }, [appComposed, cardHandoffOpacity, slotOpacities]);

  useEffect(() => {
    if (!transitionPending || !settled) return undefined;
    const incomingSlot = pendingSlotRef.current;
    if (incomingSlot == null) return undefined;
    const outgoingSlot = activeSlotRef.current;
    const motion = transitionMotionRef.current;
    const easing = Easing.bezier(...motion.easing);

    const animation = Animated.parallel([
      Animated.timing(slotOpacities[outgoingSlot], {
        toValue: 0,
        duration: motion.durationMs,
        easing,
        useNativeDriver: Platform.OS !== "web"
      }),
      Animated.timing(slotOpacities[incomingSlot], {
        toValue: 1,
        duration: motion.durationMs,
        easing,
        useNativeDriver: Platform.OS !== "web"
      })
    ]);
    animationRef.current = animation;
    animation.start(({ finished }) => {
      if (!finished) return;
      animationRef.current = null;
      activeSlotRef.current = incomingSlot;
      pendingSlotRef.current = null;
      setTransitionPending(false);
    });
    return () => animation.stop();
  }, [settled, slotOpacities, transitionPending]);

  useEffect(() => {
    if (!cardHandoffUri || !settled) return undefined;
    const easing = Easing.bezier(...transitionMotionRef.current.easing);
    const animation = Animated.timing(cardHandoffOpacity, {
      toValue: 0,
      duration: CARD_BACKDROP_REVEAL_MS,
      easing,
      useNativeDriver: Platform.OS !== "web"
    });
    cardHandoffAnimationRef.current = animation;
    animation.start(({ finished }) => {
      if (cardHandoffAnimationRef.current === animation) {
        cardHandoffAnimationRef.current = null;
      }
      if (finished) setCardHandoffUri(null);
    });
    return () => animation.stop();
  }, [cardHandoffOpacity, cardHandoffUri, settled]);

  const onLoad = useCallback((slot: number, loadedUri: string) => {
    if (slotUrisRef.current[slot] !== loadedUri) return;
    loadedSlotUrisRef.current[slot] = loadedUri;
    markImageDecoded(loadedUri);
    if (targetUriRef.current === loadedUri) setSettled(true);
  }, []);

  const onError = useCallback((slot: number, failedUri: string) => {
    if (slotUrisRef.current[slot] !== failedUri) return;
    loadedSlotUrisRef.current[slot] = null;
    setSlots((current) => {
      const updated = [...current] as [BackdropSlotState, BackdropSlotState];
      updated[slot] = { failed: true, uri: failedUri };
      return updated;
    });
    if (targetUriRef.current === failedUri) setSettled(true);
  }, []);

  const value = useMemo<BackdropContextValue>(
    () => ({
      cardHandoff: {
        opacity: cardHandoffOpacity,
        uri: cardHandoffUri
      },
      focalY,
      onError,
      onLoad,
      request,
      slots: slots.map((slot, index) => ({
        ...slot,
        opacity: slotOpacities[index]
      }))
    }),
    [
      cardHandoffOpacity,
      cardHandoffUri,
      focalY,
      onError,
      onLoad,
      request,
      slotOpacities,
      slots
    ]
  );

  return <BackdropContext.Provider value={value}>{children}</BackdropContext.Provider>;
}

/** Publish this route's desired backdrop whenever it gains focus. */
export function usePageBackdrop(
  uri: string | null,
  /**
   * Vertical focal anchor for this route's artwork, 0-100. Omit for the centred
   * default — only pass it where the artwork demands it, e.g. a program cover
   * whose wordmark sits mid-frame (COVER_ARTWORK_FOCAL_Y).
   */
  focalY?: number | null
): void {
  const context = useContext(BackdropContext);
  if (!context) throw new Error("usePageBackdrop requires PageBackdropProvider");
  const request = context.request;

  useFocusEffect(
    useCallback(() => {
      request(uri, focalY ?? null);
    }, [focalY, request, uri])
  );
}

/**
 * While one artwork-backed route resolves into another, cards briefly share
 * the departing page atmosphere by remaining transparent, then reveal their
 * own established backdrops on the same curve as the page-level crossfade.
 */
export function useCardBackdropHandoff(): BackdropContextValue["cardHandoff"] {
  const context = useContext(BackdropContext);
  if (!context) {
    throw new Error("useCardBackdropHandoff requires PageBackdropProvider");
  }
  return context.cardHandoff;
}

/** The single full-bleed backdrop mounted underneath the route stack. */
export function PageBackdrop({
  height = 460,
  intensity = 1,
  ownsReadiness = false,
  sourceUri,
  focalY
}: {
  height?: number;
  intensity?: number;
  ownsReadiness?: boolean;
  sourceUri?: string | null;
  /** Route-local anchor for the `sourceUri` path, which paints before focus. */
  focalY?: number | null;
}) {
  const context = useContext(BackdropContext);
  if (!context) throw new Error("PageBackdrop requires PageBackdropProvider");

  const viewport = useWindowDimensions();
  const contentPosition = artworkContentPosition(focalY ?? context.focalY);
  // expo-image keeps its native renderer alive while the window rotates. A
  // non-centred contentPosition can otherwise retain the old cover crop and
  // expose the image view's black backing surface. Preserve the stable outer
  // crossfade slots, but replace/reset only the cached native image whenever
  // its viewport geometry changes so cover + focal positioning is recomputed.
  const nativeGeometryKey = Platform.OS === "web"
    ? "web"
    : `${Math.round(viewport.width)}x${Math.round(viewport.height)}`;

  // Web's full-page backdrop is an imperative body sibling so Expo Router's
  // browser-history layout remounts cannot remove it. Header copies still use
  // the shared React slots below.
  if (Platform.OS === "web" && ownsReadiness) return null;

  // Native-stack screens render this route-local copy with their own URI
  // before push/pop reveals them. They therefore cannot inherit the
  // previously focused route's artwork while waiting for a focus event.
  if (sourceUri !== undefined) {
    return (
      <View style={[styles.pageBackdrop, { height, pointerEvents: "none" }]}>
        {sourceUri ? (
          <ExpoImage
            key={`route-backdrop-${nativeGeometryKey}`}
            cachePolicy="memory-disk"
            contentFit="cover"
            contentPosition={contentPosition}
            recyclingKey={`route-backdrop-${nativeGeometryKey}`}
            source={brandBackdropSource(sourceUri)}
            blurRadius={60}
            transition={0}
            style={[
              styles.pageBackdropImage,
              { opacity: 0.45 * intensity }
            ]}
          />
        ) : null}
        <BackdropGradient />
      </View>
    );
  }

  return (
    <View style={[styles.pageBackdrop, { height, pointerEvents: "none" }]}>
      {context.slots.map((slot, index) =>
        slot.uri && !slot.failed ? (
          // RN's Image has no object-position, so the focal anchor comes from
          // Expo Image's contentPosition. The crossfade keeps driving opacity on
          // a wrapper View, leaving the slot's animated value and its stable key
          // exactly as the history-traversal machinery below expects. Web stays
          // on Animated.Image: react-native-web's blurRadius path is what the
          // header copies already render, and web's full-page backdrop is the
          // DOM compositor, which carries its own background-position.
          <Animated.View
            key={`page-backdrop-slot-${index}`}
            style={[
              styles.pageBackdropImage,
              { opacity: Animated.multiply(slot.opacity, 0.45 * intensity) }
            ]}
          >
            {Platform.OS === "web" ? (
              <Animated.Image
                source={brandBackdropSource(slot.uri)}
                resizeMode="cover"
                blurRadius={60}
                fadeDuration={0}
                onLoad={ownsReadiness ? () => context.onLoad(index, slot.uri!) : undefined}
                onError={ownsReadiness ? () => context.onError(index, slot.uri!) : undefined}
                style={StyleSheet.absoluteFill}
              />
            ) : (
              <ExpoImage
                key={`page-backdrop-${index}-${nativeGeometryKey}`}
                source={brandBackdropSource(slot.uri)}
                cachePolicy="memory-disk"
                contentFit="cover"
                contentPosition={contentPosition}
                recyclingKey={`page-backdrop-${index}-${nativeGeometryKey}`}
                blurRadius={60}
                transition={0}
                onLoad={ownsReadiness ? () => context.onLoad(index, slot.uri!) : undefined}
                onError={ownsReadiness ? () => context.onError(index, slot.uri!) : undefined}
                style={StyleSheet.absoluteFill}
              />
            )}
          </Animated.View>
        ) : null
      )}
      <BackdropGradient />
    </View>
  );
}

function BackdropGradient() {
  return (
    <LinearGradient
      colors={pageScrimStops()}
      locations={[0, PAGE_SCRIM_MID_STOP / 100, 1]}
      style={StyleSheet.absoluteFill}
    />
  );
}

const styles = StyleSheet.create({
  pageBackdrop: {
    left: 0,
    overflow: "hidden",
    position: "absolute",
    right: 0,
    top: 0
  },
  pageBackdropImage: {
    height: "115%",
    left: "-6%",
    position: "absolute",
    top: "-8%",
    width: "112%"
  }
});
